/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactNode} from 'react';
import {createTheme, Theme, ThemeProvider, useTheme} from '@material-ui/core/styles';
import {useSignals} from '@preact/signals-react/runtime';
import {getCurrentDarkPalette} from '../theme/theme';
import {darkModeEnabled} from '../theme/ThemeStore';
import {useThemeMode} from '../theme/useThemeContext';

type ThemedRootProps = {
  children?: ReactNode;
};

/**
 * The dark Material UI theme shared by every themed root. Created once, on
 * first use, from the Material UI default theme and the dark theme's palette
 * only.
 */
let darkTheme: Theme | undefined;

/**
 * Returns the shared dark Material UI theme, creating it on first use. Returns
 * `undefined` when the dark theme provides no palette (see
 * `getCurrentDarkPalette`), so callers fall back to the light theme.
 */
const getDarkTheme = (): Theme | undefined => {
  const darkPalette = getCurrentDarkPalette();
  if (!darkTheme && darkPalette) {
    darkTheme = createTheme({palette: darkPalette});
  }
  return darkTheme;
};

/**
 * The `ThemedRoot` component provides the Material UI theme for the effective
 * theme mode to a separate React root, such as a map popup or an on-map panel
 * rendered via the {@link RootManager}, once the application has opted in to
 * dark mode (see `darkModeEnabled`).
 *
 * Until the application opts in, `ThemedRoot` renders its children alone, with
 * no theme provider, so the root's tree, and the class names of its Material
 * UI styles, are those of a root rendered without `ThemedRoot`.
 *
 * Once the application opts in, `ThemedRoot` renders a theme provider. In light
 * mode, the provided theme is the Material UI default theme which the root's
 * styles fall back to without a provider, so light output is unchanged. In dark
 * mode, the provided theme is the Material UI default theme with the dark
 * palette applied. The provider stays mounted while dark mode is enabled, so a
 * mode change re-themes an open root without remounting it (or reloading its
 * iframes).
 *
 * **Note, `ThemedRoot` is applied by `RootManager.render`; implementations need
 * not render it directly. Applications opt in once, at startup; a change to the
 * opt-in adds or removes the provider, which may remount an open root.**
 *
 * ```typescript
 *   import ThemedRoot from '@ion/dls/src/client/ThemedRoot';
 *
 *   ...
 *
 *   // render a component with the theme of the effective theme mode ...
 *   root.render(createElement(ThemedRoot, null, <AssetPopup asset={asset} />));
 * ```
 *
 * @param children The React Node which is to be rendered with the theme.
 */
const ThemedRoot = ({children}: ThemedRootProps) => {
  useSignals();
  const lightTheme = useTheme();
  const {isDark}   = useThemeMode();

  const theme = (isDark && getDarkTheme()) || lightTheme;
  return darkModeEnabled.value ? (
    <ThemeProvider theme={theme}>
      {children}
    </ThemeProvider>
  ) : (
    <>
      {children}
    </>
  );
};

export default ThemedRoot;
